import {useLayoutEffect, useState} from 'react';
import queryString from 'query-string';
import _ from 'lodash';
import moment from 'moment';

import Config from '../utils/Configuration';

const getValues = (source, variables = '', fallbackValue = null, allowNull = false) => {
    const targetValueHierarchy = (variables || '')
        .toString()
        .replace(/[[\]]/g, '.')
        .split('.')
        .filter((key) => key !== '');

    if (source === null && allowNull && targetValueHierarchy.length === 0) {
        return null;
    }

    // Check for string type because string is subtype of Array
    // Don't worry, if the data type not an object or array will fail after that.
    if (!source || ['string, boolean'].includes(typeof source)) {
        return fallbackValue;
    }

    // Retain data type cause data type is dynamic
    let result = Object.assign(source);

    for (let i = 0; i < targetValueHierarchy.length; i++) {
        result = result[targetValueHierarchy[i]];

        if (result === undefined) {
            break;
        }

        if (result === null && i !== targetValueHierarchy.length - 1) {
            result = undefined;
            break;
        }
    }

    if (result === null) {
        return allowNull ? result : fallbackValue;
    }

    return result !== undefined ? result : fallbackValue;
};

const getRouteComponentProps = (routeConfig, routerProps, {isStandalone = true, language = 'en'} = {}) => {
    let [splitHash, splitQuery] = [null, null];
    if (routerProps.location.hash) {
        [splitHash, splitQuery] = routerProps.location.hash.split('?');
    }

    const routesAdditionalProps = routeConfig.additionalProps;
    const urlQueryString = routerProps.location.search.substring(1);
    const queryParams = queryString.parse(splitQuery || urlQueryString);
    const childrenRoutes = (routeConfig.routes || []).length > 0 ? {childrenRoutes: routeConfig.routes} : {};

    return {
        language,
        queryParams,
        hash: splitHash || routerProps.location.hash,
        standaloneComponent: Boolean(isStandalone),
        ...childrenRoutes,
        ...routesAdditionalProps,
        ...routerProps.location.state,
        ...getValues(routerProps, 'match.params', {})
    };
};

const getCookie = (cname) => {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookiesArray = decodedCookie.split(';');
    let cookie = '';
    cookiesArray.forEach((cookies) => {
        let cookieItem = cookies;
        while (cookieItem.charAt(0) === ' ') {
            cookieItem = cookieItem.substring(1);
        }
        if (cookieItem.indexOf(name) === 0) {
            cookie = cookieItem.substring(name.length, cookieItem.length);
        }
    });

    // This code will help to check if cookie contains a valid JSON or not.
    try {
        return JSON.parse(cookie);
    } catch {
        return cookie;
    }
};

const saveCookie = (name, value, option) => {
    const {maxAgeInSecond, path = '/', domain = window.location.hostname, useCookieEncoding = false} = option;
    const oldCookies = domain === window.location.hostname ? getCookie(name) : '';
    let formattedValue = domain === window.location.hostname ? JSON.stringify({...oldCookies, ...value}) : value;
    formattedValue = useCookieEncoding ? encodeURI(formattedValue) : formattedValue;
    let expires = '';

    if (maxAgeInSecond) {
        const date = new Date();
        date.setTime(date.getTime() + maxAgeInSecond * 1000);
        expires = `;expires=${date.toGMTString()}`;
    }
    document.cookie = `${name}=${formattedValue}${expires};path=${path};domain=${domain}`;
};

const deleteCookie = (cname, option = {}) => {
    const {path = '/', domain = window.location.hostname} = option;
    const date = new Date();
    date.setTime(date.getTime() + -100 * 24 * 60 * 1000);
    const expires = `;expires=${date.toGMTString()}`;
    document.cookie = `${cname}=${expires};path=${path};domain=${domain}`;
};

const getAemUrl = (url) => {
    return url ? `${Config.apiConfig.aemEndPoint}${url}` : url;
};

const useWindowSize = () => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
};

const textTruncate = (str, length = 100, suffix = '...') => {
    if (str && str.length > length) {
        return str.substring(0, length) + suffix;
    }
    return str;
};

const navigateTo = (url, navigate = () => {}) => {
    let link = String(url || '');

    // for internal navigation between pages - useNavigation hook in parent component and pass argument here
    if (link.startsWith('/') || link === '-1') {
        navigate(url);
    } else if (link.includes('maxis.com.my')) {
        window.location = link;
    } else {
        window.open(link);
    }
};

const seoAltTextGeneratorWithFilePath = (
    filepath = '',
    {prefix = Config.seoAltTextPrefixes.maxis, isTransformCapitalize = true} = {}
) => {
    const uri = filepath.replace(/[_-]/g, ' ').split('/');
    let fileName = (uri[uri.length - 1].split('.')[0] || '').toLowerCase();

    if (isTransformCapitalize) {
        fileName = fileName.replace(/\b\w/g, (c) => c.toUpperCase());
    }

    return [prefix, fileName].filter(Boolean).join(' ');
};

const addressFormatPostProcessor = (address = '') => {
    // remove pipe symbol and extra space from address
    return address.replaceAll('|', ' ').replaceAll(/\s+/g, ' ');
};

const identifiedCartOrderType = (cartDetails = {}) => {
    return cartDetails.orderType || Config.orderType.NORMAL_ORDER;
};

const fibreCoverageCookiesGenerator = (data) => {
    return {
        Fibre_PlanID: data.fibrePlanId,
        raw_address: {
            [Config.maxisFibreCookieKeys.ADDRESS1]: data.address1 || '',
            [Config.maxisFibreCookieKeys.ADDRESS2]: data.address2 || '',
            [Config.maxisFibreCookieKeys.ADDRESS3]: data.address3 || '',
            [Config.maxisFibreCookieKeys.BUILDINGNAME]: data.buildingName || '',
            [Config.maxisFibreCookieKeys.CITY]: data.city || '',
            [Config.maxisFibreCookieKeys.FLOORNO]: data.floor || '',
            [Config.maxisFibreCookieKeys.POSTCODE]: data.postCode || '',
            [Config.maxisFibreCookieKeys.SECTION]: data.section || '',
            [Config.maxisFibreCookieKeys.STATE]: data.state || '',
            [Config.maxisFibreCookieKeys.STREETNAME]: data.streetName || '',
            [Config.maxisFibreCookieKeys.STREETTYPE]: data.streetType || '',
            [Config.maxisFibreCookieKeys.TAG]: data.tag || '',
            [Config.maxisFibreCookieKeys.TYPE]: data.type || '',
            [Config.maxisFibreCookieKeys.UPCOMING]: false,
            [Config.maxisFibreCookieKeys.GPS]: data.location ? {lat: data.location.lat, lng: data.location.long} : null,
            [Config.maxisFibreCookieKeys.PROJECTTYPE]: data.projectType || '',
            [Config.maxisFibreCookieKeys.FULLADDRESS]: data.address || ''
        },
        tag: data.tag || ''
    };
};

const tryJsonParse = (v = '') => {
    try {
        return JSON.parse(v);
    } catch (ex) {
        return null;
    }
};

export const fibreToCartRedux = (plan) => {
    let cartReduxPayload = {
        orderType: Config.orderType.ENTERPRISE_FIBRE_ORDER,
        serviceType: plan.serviceType,
        contractTerm: plan.contractTerm,
        solutionName: plan.solutionName,
        planImagePath: getValues(plan, 'planDetails.planIconPath', null),
        planName: plan.planName,
        ipType: plan.ipType,
        planSalesforceId: plan.salesforceId,
        planBandwidth: plan.bandwidth,
        voipSalesforceId: getValues(plan, 'voicePlans.salesforceId', ''),
        voicePlanName: getValues(plan, 'voicePlans.name', ''),
        voicePlanPrice: getValues(plan, 'voicePlans.price', null),
        mainPackageTitle: getValues(plan, 'planInfo.mainPackage.title', ''),
        planPrice: plan.planPrice,
        brand: Config.maxisBrand,
        planId: plan.planId,
        intention: Config.flowType.addNewLine,
        devicesBundle: plan.devicesBundle
    };
    if (!_.isEmpty(plan.planPromotion)) {
        cartReduxPayload = {
            ...cartReduxPayload,
            promoMessage: getValues(plan, 'planPromotion[0].promotionBannerMessage', '')
        };
    }
    return cartReduxPayload;
};

const formatPriceWithCommas = (priceString = '') => {
    return parseFloat(priceString).toLocaleString('en-US', {maximumFractionDigits: 2, minimumFractionDigits: 0});
};

const openExternalLink = (url = '', {openMethod = '_blank'} = {}) => {
    if (!url) return;

    window.open(url, openMethod);
};

const generateWhatsappLink = (message = '', {phoneNumber} = {}) => {
    return [
        Config.apiConfig.maxisWhatsAppLiveChat.sendRequestUrl,
        queryString.stringify({phone: phoneNumber || Config.apiConfig.maxisWhatsAppLiveChat.phoneNumber, text: message})
    ].join('?');
};

const whatsAppUsNow = (message = '', {phoneNumber, openMethod = '_blank'} = {}) => {
    const whatsAppLink = generateWhatsappLink(message, {phoneNumber});

    openExternalLink(whatsAppLink, {openMethod});
};

const getCartItemDetails = (cartDetails) => {
    if (!_.isEmpty(cartDetails)) {
        const {cartItems} = {...cartDetails};
        return (cartItems || []).map((item) => {
            const {cartItemId, lines} = item;
            return {
                cartItemId,
                lines: (lines || []).map((line) => {
                    return {...line};
                })
            };
        });
    }
    return [];
};

const convertYYYYMMDDHHmmssDate = (dtStr, dateFormat = 'DD / MM / YYYY  HH : mm') => {
    return dtStr ? moment(dtStr, 'YYYYMMDDHHmmss', false).format(dateFormat) : '';
};

const scrollToTop = () => {
    window.scrollTo(0, 0);
};

const utility = {
    getValues,
    getRouteComponentProps,
    getCookie,
    saveCookie,
    deleteCookie,
    getAemUrl,
    useWindowSize,
    textTruncate,
    navigateTo,
    seoAltTextGeneratorWithFilePath,
    addressFormatPostProcessor,
    identifiedCartOrderType,
    fibreCoverageCookiesGenerator,
    whatsAppUsNow,
    getCartItemDetails,
    tryJsonParse,
    fibreToCartRedux,
    formatPriceWithCommas,
    convertYYYYMMDDHHmmssDate,
    scrollToTop
};

export default utility;
